import React, { useState } from 'react';
import {
  Box,
  TextField,
  Checkbox,
  Button,
  FormControlLabel,
  Container,
  Typography,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import { getResponsiveHeight, getResponsiveSize } from '../../utils/responsive';
import globalStyles from '../../global';
import { eventAPI } from '../../utils/api'; // Assuming you have a countries.js file with country data
import { countriesData } from '../../utils/helper';
import { Search } from 'lucide-react';

const EventForm = ({ seminarPlace = 'Doha' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [countries, setCountries] = useState(countriesData);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: '240px',
      },
    },
  };

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    countryCode: '+1',
    city: '',
    consultationTypes: seminarPlace,
    agreeToPrivacyPolicy: false,
  });
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    console.log('Form submitted:', formData);

    if (!formData.agreeToPrivacyPolicy) {
      alert('Please agree to the privacy policy.');
      return;
    }

    setSubmitStatus(null);

    const result = await eventAPI.submitEventRegistration(formData);

    if (result.success) {
      setSubmitStatus('success');
      // Clear the form, including checkboxes
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        countryCode: '+1',
        city: '',
        consultationTypes: [],
        agreeToPrivacyPolicy: false,
      });
    } else {
      setSubmitStatus('error');
      console.error('API Error:', result.error);
    }
  };

  // Responsive font size
  const inputFontSize = {

              xs: "14px",
              md: "18px",
              lg: "18px",
       
  };

  // Common TextField styles
  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: getResponsiveSize(12),
      fontFamily: '"Poppins", serif',
      fontWeight: 500,
      fontSize: inputFontSize,
      '& fieldset': {
        borderColor: globalStyles.colors.border_gray,
        fontFamily: '"Poppins", serif',
        fontWeight: 400,
      },
      '&:hover fieldset': {
        borderColor:  globalStyles.colors.uclean_green,
      },
      '&.Mui-focused fieldset': {
        borderColor:  globalStyles.colors.uclean_green,
      },
      // Ensures the placeholder text uses the Poppins font
      '& .MuiInputBase-input::placeholder': {
        fontFamily: '"Poppins", serif',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: inputFontSize,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color:  globalStyles.colors.uclean_green,
      fontFamily: '"Poppins", serif',
      fontWeight: 500,
    },
  };

  const handleInputChange = (e) => {
    const { value } = e.target;
    const filteredCountries = countriesData.filter((country) => {
      return country.name.toLowerCase().includes(value.toLowerCase());
    });
    setCountries(filteredCountries);
  };

  return (
    <Container sx={{ padding: isMobile ? '0 !important' : '' }}>
      <Card
        sx={{
          px: getResponsiveHeight(isMobile ? 2 : 4),
          mt: getResponsiveHeight(4),
          borderRadius: getResponsiveSize(8),
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        }}
      >
        <CardContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mb: 2,
                flexDirection: isMobile ? 'column' : 'row',
              }}
            >
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="First Name"
                required
                sx={{ ...textFieldStyles, flex: 1 }}
              />
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Last Name"
                required
                sx={{ ...textFieldStyles, flex: 1 }}
              />
            </Box>

            {/* Email & Phone */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mb: 2,
                flexDirection: isMobile ? 'column' : 'row',
              }}
            >
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email"
                required
                sx={{ ...textFieldStyles, flex: 1 }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                mb: 2,
                flexDirection: isMobile ? 'column' : 'row',
              }}
            >
              <FormControl sx={{ ...textFieldStyles, flex: 1 }}>
                <InputLabel id="country-code-label">Country Code</InputLabel>
                <Select
                  labelId="country-code-label"
                  id="country-code"
                  name="countryCode"
                  value={formData.countryCode}
                  label="Country Code"
                  onChange={handleChange}
                  MenuProps={MenuProps}
                  sx={{
                    width: isMobile ? '100%' : '100%',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center',
                      pl: 2,
                      py: 1,
                    }}
                  >
                    <Search size={'18px'} />
                    <input
                      type="text"
                      onChange={handleInputChange}
                      placeholder="Search"
                      style={{
                        border: `1px solid ${globalStyles.colors.uclean_green} `,
                        outline: 'none',
                        backgroundColor: 'transparent',
                        width: '70%',
                        height: '24px',
                        borderRadius: '8px',
                        fontFamily: '"Poppins", serif',
                        fontSize: inputFontSize,
                        fontWeight: 500,

                      }}
                    />
                  </Box>
                  {countries.map((country) => (
                    <MenuItem key={country.code} value={country.dial_code}>
                      {country.dial_code} {country.flag} {country.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Phone Number"
                sx={{
                  ...textFieldStyles,
                  flex: 1,
                  width: isMobile ? '100%' : '70%',
                  marginLeft: isMobile ? 0 : '1rem',
                }}
              />
            </Box>
            {/* City Field */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mb: 2,
                flexDirection: isMobile ? 'column' : 'row',
              }}
            >
              <TextField
                fullWidth
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="City"
                required
                sx={{ ...textFieldStyles, flex: 1 }}
              />
            </Box>

            {/* Privacy Policy Agreement */}
            <FormControlLabel
              control={
                <Checkbox
                  name="agreeToPrivacyPolicy"
                  checked={formData.agreeToPrivacyPolicy}
                  onChange={handleChange}
                  sx={{
                    color:  globalStyles.colors.uclean_green,
                    '&.Mui-checked': {
                      color: globalStyles.colors.uclean_green,
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(184, 134, 11, 0.04)',
                    },
                  }}
                />
              }
              label={
                <Typography
                  variant="regular_text_ur"
                  sx={{ fontSize: inputFontSize }}
                >
                  By checking this box, I agree to the UClean Privacy Policy
                </Typography>
              }
              sx={{ mb: 2 }}
            />

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 2,
                mb: 2,
                bgcolor: globalStyles.colors.uclean_green,
                '&:hover': {
                  bgcolor:   globalStyles.colors.army_green,
                },
               
                py: 1.5,
              }}
            >
              Register Now
            </Button>

            {submitStatus === 'success' && (
              <Typography color="green" sx={{ mt: 1 }}>
                Form submitted successfully!
              </Typography>
            )}
            {submitStatus === 'error' && (
              <Typography color="red" sx={{ mt: 1 }}>
                Error submitting form. Please try again.
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default EventForm;
